'use client';
// AmanChain Bridge (v2.8) — move value between external chains and AmanChain
// Solana · Ethereum · BNB · Polygon · Arbitrum · Base · Avalanche · Optimism ·
// Bitcoin · Litecoin — REAL vaults, REAL verification, REAL broadcast,
// honest operator queue for UTXO chains, honest statuses everywhere.
import { useState } from 'react';
import { ArrowLeftRight, Copy, ExternalLink, Loader2, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import { fetchJson, usePoll, post, Mono, SectionHeader, CopyBtn, EmptyState } from '@/components/chain/shared';
import { useSession, ConnectPrompt } from '@/components/chain/session';
import type { Net } from '@/components/chain/types';

interface Health { net: string; online: boolean; latencyMs: number | null; tip: string | null; error: string | null; }
interface NetView {
  net: string;
  name: string; nativeSymbol: string;
  vaultAddress: string; explorerAddr: string;
  health: Health; balances: { asset: string; amount: number; decimals: number }[] | null;
  balanceError: string | null;
  assets: { symbol: string; kind: 'native' | 'token'; decimals: number }[];
}
interface RecordT {
  id: string; net: string; dir: 'in' | 'out'; asset: string; networkName: string;
  amount: number; credit: number; amanAddr: string; externalAddr: string | null;
  externalTxid: string | null; internalTxId: string | null; explorerTx: string | null;
  status: string; note: string | null; confirmations: number | null;
  attempts: number; createdAt: number; updatedAt: number;
}
interface BridgeView {
  launched?: boolean;
  feeBps: number; networks: NetView[];
  custody: { address: string; aman: number; assets: Record<string, number> };
  custodyWalletId: string;
  records: RecordT[]; height: number; error?: string;
}

const STATUS_STYLE: Record<string, { color: string; label: string }> = {
  verifying: { color: 'text-sky-400', label: 'Verifying on-chain' },
  confirmed: { color: 'text-amber-400', label: 'Confirmed — crediting' },
  settled: { color: 'text-emerald-400', label: 'Settled ✓' },
  rejected: { color: 'text-rose-400', label: 'Rejected' },
  awaiting: { color: 'text-sky-400', label: 'Awaiting release broadcast' },
  releasing: { color: 'text-amber-400', label: 'Releasing to external chain' },
  released: { color: 'text-emerald-400', label: 'Released ✓' },
  queued: { color: 'text-violet-400', label: 'Operator queue' },
  refunded: { color: 'text-emerald-400', label: 'Refunded ✓' },
};
const fmtAmt = (n: number) => n >= 1 ? n.toFixed(4).replace(/\.?0+$/, '') : n.toFixed(6).replace(/\.?0+$/, '');

export function Bridge({ net }: { net: Net }) {
  const { session } = useSession();
  const [mode, setMode] = useState<'deposit' | 'withdraw'>('deposit');
  const [selNet, setSelNet] = useState<NetView['net']>('solana');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err' | 'info'; text: string } | null>(null);

  // deposit form
  const [depAsset, setDepAsset] = useState('');
  const [txid, setTxid] = useState('');
  // withdraw form
  const [asset, setAsset] = useState('');
  const [amount, setAmount] = useState('');
  const [externalAddr, setExternalAddr] = useState('');

  const { data, refresh } = usePoll<BridgeView>(
    async () => fetchJson(`/api/bridge?net=${net}${session ? `&addr=${encodeURIComponent(session.address)}` : ''}`),
    5000,
    [net, session?.address],
  );

  const view = data && !data.error ? data : null;
  const current = view?.networks.find(n => n.net === selNet);
  const depositAsset = depAsset || current?.assets[0]?.symbol || '';
  const withdrawAsset = asset || current?.assets[0]?.symbol || '';
  const custHeld = view?.custody?.assets?.[withdrawAsset] ?? 0;

  const deposit = async () => {
    if (!session) return;
    if (!txid.trim()) { setMsg({ kind: 'err', text: 'Paste the transaction id (signature) from your external wallet first' }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await post<{ record: RecordT }>('/api/bridge', { net, action: 'verify_deposit', network: selNet, asset: depositAsset, txid: txid.trim(), amanAddr: session.address });
      setMsg({ kind: 'info', text: `Deposit #${r.record.id.slice(3, 11)} submitted — the watcher verifies it on-chain and credits your wallet automatically` });
      setTxid('');
      refresh();
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Verification failed' });
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    if (!session) return;
    const amt = Number(amount);
    if (!(amt > 0)) { setMsg({ kind: 'err', text: 'Enter the amount to withdraw' }); return; }
    if (!externalAddr.trim()) { setMsg({ kind: 'err', text: 'Enter your external destination address' }); return; }
    setBusy(true); setMsg(null);
    try {
      // 1) burn: transfer the wrapped asset from the session wallet to bridge custody
      const tx = await post<{ id: string }>(`/api/tx?net=${net}`, {
        walletId: session.walletId, password: session.password,
        type: 'token_transfer', symbol: withdrawAsset, to: view!.custody!.address, amount: amt,
      });
      // 2) arm the external release against that burn
      const r = await post<{ record: RecordT }>('/api/bridge', { net, action: 'release', network: selNet, asset: withdrawAsset, amount: amt, externalAddr: externalAddr.trim(), burnTxId: tx.id });
      setMsg({ kind: 'info', text: `Release #${r.record.id.slice(3, 11)} armed — the vault broadcasts your ${withdrawAsset} automatically (retries every 30s until it lands)` });
      setAmount('');
      refresh();
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Release failed' });
    } finally { setBusy(false); }
  };

  if (!session) {
    return (
      <div className="space-y-4">
        <SectionHeader icon={ArrowLeftRight} title="Bridge" desc="Move value between AmanChain and 10 real external networks — Solana, Ethereum, BNB, Polygon, Arbitrum, Base, Avalanche, Optimism, Bitcoin, Litecoin — with real on-chain verification." />
        <ConnectPrompt what="the Bridge" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionHeader icon={ArrowLeftRight} title="Bridge" desc="Real cross-chain custody: deposits are verified on the external chain, withdrawals are broadcast from the vault's real keys. Every status below is the truth — no fake confirmations."
        action={<span className="text-[0.7rem] font-bold rounded-full ring-1 ring-border px-2.5 py-1 text-muted-foreground">Bridge fee {((view?.feeBps ?? 50) / 100).toFixed(2)}%</span>} />

      {/* Network cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {(view?.networks ?? []).map(n => {
          const active = n.net === selNet;
          const native = n.balances?.find(b => b.asset === n.nativeSymbol);
          return (
            <button key={n.net} onClick={() => { setSelNet(n.net); setAsset(n.assets[0]?.symbol ?? ''); setMsg(null); }}
              className={`text-left rounded-2xl p-3.5 ring-1 transition ${active ? 'ring-emerald-500/50 bg-emerald-500/5' : 'ring-border hover:bg-muted/40'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-black text-sm">{n.name}</span>
                <span className={`inline-flex items-center gap-1 text-[0.62rem] font-bold ${n.health.online ? 'text-emerald-400' : 'text-rose-400'}`} title={n.health.error ?? n.health.latencyMs + 'ms'}>
                  <span className={`size-1.5 rounded-full ${n.health.online ? 'bg-emerald-400 pulse-dot' : 'bg-rose-400'}`} />
                  {n.health.online ? 'ONLINE' : 'OFFLINE'}
                </span>
              </div>
              <div className="mt-1 text-[0.68rem] text-muted-foreground">
                Vault {native ? <><b className="text-foreground">{fmtAmt(native.amount)} {n.nativeSymbol}</b></> : (n.balanceError ? 'balance n/a' : '…')}
              </div>
              <Mono className="mt-1.5 block text-[0.62rem] text-muted-foreground truncate">{n.vaultAddress}</Mono>
            </button>
          );
        })}
      </div>

      {/* Deposit / Withdraw switch */}
      <div className="flex items-center gap-2">
        {(['deposit', 'withdraw'] as const).map(m => (
          <button key={m} onClick={() => { setMode(m); setMsg(null); }}
            className={`rounded-xl px-4 py-2 text-xs font-black transition ${mode === m ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40' : 'text-muted-foreground ring-1 ring-border hover:text-foreground'}`}>
            {m === 'deposit' ? `Deposit to AmanChain` : `Withdraw to ${current?.name ?? 'external chain'}`}
          </button>
        ))}
      </div>

      {/* Vault address (deposit) */}
      {mode === 'deposit' && current && (
        <div className="rounded-2xl ring-1 ring-border p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-black">Deposit {depositAsset} on {current.name} to the bridge vault</div>
            <CopyBtn text={current.vaultAddress} />
          </div>
          <div className="max-w-xs">
            <label className="text-xs font-bold text-muted-foreground">Asset you are sending</label>
            <select value={depositAsset} onChange={e => setDepAsset(e.target.value)}
              className="mt-1.5 w-full rounded-xl bg-background ring-1 ring-input px-3 py-2.5 text-sm outline-none focus:ring-emerald-500/50">
              {current.assets.map(a => <option key={a.symbol} value={a.symbol}>{a.symbol}{a.kind === 'token' ? ' (token)' : ''}</option>)}
            </select>
          </div>
          <Mono className="block text-xs break-all rounded-xl bg-muted/60 p-3">{current.vaultAddress}</Mono>
          <div className="flex items-center gap-3 flex-wrap text-[0.7rem] text-muted-foreground">
            <a href={current.explorerAddr} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground"><ExternalLink className="size-3" /> view vault on {current.name} explorer</a>
            <span>·</span>
            <span>watcher {current.health.online ? `online (${current.health.latencyMs}ms)` : `offline — deposits verify when it reconnects`}</span>
            <span>·</span>
            <span>credit = observed amount − {((view?.feeBps ?? 50) / 100).toFixed(2)}% fee</span>
          </div>
          <div className="pt-1">
            <label className="text-xs font-bold text-muted-foreground">After sending, paste the transaction id / signature</label>
            <div className="mt-1.5 flex gap-2">
              <input value={txid} onChange={e => setTxid(e.target.value)} placeholder="e.g. 5Kd3…(Solana signature) or 0x… (EVM hash)"
                className="flex-1 rounded-xl bg-background ring-1 ring-input px-3.5 py-2.5 text-sm outline-none focus:ring-emerald-500/50 font-mono" dir="ltr" />
              <button onClick={deposit} disabled={busy}
                className="rounded-xl bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40 px-4 py-2.5 text-xs font-black hover:bg-emerald-500/25 disabled:opacity-50 inline-flex items-center gap-2">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />} Verify & credit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw */}
      {mode === 'withdraw' && current && (
        <div className="rounded-2xl ring-1 ring-border p-4 space-y-3">
          <div className="text-sm font-black">Burn wrapped {withdrawAsset} on AmanChain → receive real {withdrawAsset} on {current.name}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-muted-foreground">Asset</label>
              <select value={withdrawAsset} onChange={e => setAsset(e.target.value)}
                className="mt-1.5 w-full rounded-xl bg-background ring-1 ring-input px-3 py-2.5 text-sm outline-none focus:ring-emerald-500/50">
                {current.assets.map(a => <option key={a.symbol} value={a.symbol}>{a.symbol}{a.kind === 'token' ? ' (token)' : ''} — custody float {fmtAmt(view?.custody.assets?.[a.symbol] ?? 0)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-muted-foreground">Amount</label>
              <input value={amount} onChange={e => setAmount(e.target.value)} inputMode="decimal" placeholder={`0.00 ${withdrawAsset}`}
                className="mt-1.5 w-full rounded-xl bg-background ring-1 ring-input px-3.5 py-2.5 text-sm outline-none focus:ring-emerald-500/50" dir="ltr" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground">Your {current.name} address (destination)</label>
            <input value={externalAddr} onChange={e => setExternalAddr(e.target.value)} placeholder={current.net === 'solana' ? 'Base58 destination address' : current.net === 'bitcoin' ? 'bc1q… destination address' : current.net === 'litecoin' ? 'ltc1… destination address' : '0x… destination address'}
              className="mt-1.5 w-full rounded-xl bg-background ring-1 ring-input px-3.5 py-2.5 text-sm outline-none focus:ring-emerald-500/50 font-mono" dir="ltr" />
          </div>
          <div className="text-[0.7rem] text-muted-foreground space-y-1">
            <div>Releases broadcast from the vault&apos;s real keys. If the vault lacks funds or the watcher is offline, the release <b className="text-foreground">retries automatically every 30 seconds</b> and shows the exact reason — it never disappears.</div>
            {(current.net === 'bitcoin' || current.net === 'litecoin') && <div className="text-violet-400 font-bold">{current.nativeSymbol} releases run through the operator queue: the operator broadcasts from the vault and attaches the real txid (shown here once attached).</div>}
          </div>
          <button onClick={withdraw} disabled={busy}
            className="rounded-xl bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40 px-4 py-2.5 text-xs font-black hover:bg-emerald-500/25 disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ArrowLeftRight className="size-4" />} Burn & release {withdrawAsset}
          </button>
        </div>
      )}

      {msg && (
        <div className={`rounded-xl p-3 text-xs font-bold ring-1 ${msg.kind === 'err' ? 'bg-rose-500/10 text-rose-400 ring-rose-500/30' : msg.kind === 'ok' ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/30' : 'bg-sky-500/10 text-sky-400 ring-sky-500/30'}`}>
          {msg.kind === 'err' && <AlertTriangle className="size-3.5 inline mr-1.5 -mt-0.5" />}
          {msg.text}
        </div>
      )}

      {/* Custody float */}
      <div className="rounded-2xl ring-1 ring-border p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-black">Custody float on {net}</div>
          <button onClick={() => refresh()} className="text-[0.68rem] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RefreshCw className="size-3" /> refresh</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-[0.7rem]">
          <span className="rounded-full ring-1 ring-border px-2.5 py-1 font-bold">AMAN {fmtAmt(view?.custody?.aman ?? 0)}</span>
          {Object.entries(view?.custody?.assets ?? {}).filter(([, v]) => v > 0).map(([k, v]) => (
            <span key={k} className="rounded-full ring-1 ring-emerald-500/30 bg-emerald-500/5 text-emerald-400 px-2.5 py-1 font-bold">{k} {fmtAmt(v)}</span>
          ))}
          {Object.values(view?.custody?.assets ?? {}).every(v => !v) && (
            <span className="text-amber-400 font-bold">Float is empty — withdrawals queue honestly until the operator funds custody (Admin → Bridge)</span>
          )}
        </div>
      </div>

      {/* Records */}
      <div>
        <div className="text-sm font-black mb-2">My bridge activity</div>
        {!view?.records.length ? (
          <EmptyState icon={ArrowLeftRight} title="No bridge activity yet" desc="Deposits and withdrawals appear here with their real on-chain status." />
        ) : (
          <div className="space-y-2">
            {view.records.map(r => {
              const st = STATUS_STYLE[r.status] ?? { color: 'text-muted-foreground', label: r.status };
              return (
                <div key={r.id} className="rounded-2xl ring-1 ring-border p-3.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-sm font-black">
                      <span className={r.dir === 'in' ? 'text-emerald-400' : 'text-sky-400'}>{r.dir === 'in' ? '↓' : '↑'}</span>
                      {fmtAmt(r.dir === 'in' ? r.credit : r.amount)} {r.asset}
                      <span className="text-[0.68rem] font-bold text-muted-foreground">· {r.networkName}</span>
                    </div>
                    <span className={`text-[0.7rem] font-black ${st.color}`}>{st.label}</span>
                  </div>
                  {r.note && <div className="mt-1.5 text-[0.72rem] text-muted-foreground leading-relaxed">{r.note}</div>}
                  <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[0.66rem] text-muted-foreground">
                    <span>{new Date(r.updatedAt).toLocaleString()}</span>
                    {r.confirmations != null && <span>· {r.confirmations} conf</span>}
                    {r.attempts > 1 && <span>· {r.attempts} attempts</span>}
                    {r.explorerTx && <a href={r.explorerTx} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground"><ExternalLink className="size-3" /> external tx {r.externalTxid?.slice(0, 10)}…</a>}
                    {r.internalTxId && <span>· AmanChain tx <Mono>{r.internalTxId.slice(0, 10)}…</Mono></span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
